import { useIntl, FormattedMessage } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import { useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';

import ContentView from '~/components/ContentView';
import generalMessages from '~/containers/App/messages';
import { useUpdateExperienceTitle } from '~/hooks/api/useUpdateExperienceTitle';
import { useExperience } from '~/hooks/api/useExperience';
import EditTitleForm from './EditTitleForm';
import messages from './messages';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

const route = getRouteApi('/experiences/$experienceId/edit-title');

export const EditTitle = () => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const navigate = useNavigate();
  const { experienceId } = route.useParams();
  const experienceIdNum = parseInt(experienceId, 10);

  const { data: experience, isLoading } = useExperience({
    experienceId: experienceIdNum,
    expand: ['events', 'turn.shift', 'turn.route'],
  });

  const gotoExperienceDetails = () => {
    navigate({ to: `/experiences/${experienceIdNum}` });
  };

  const updateTitleMutation = useUpdateExperienceTitle({
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.editExperienceTitleSuccess),
      });
      gotoExperienceDetails();
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.editExperienceTitleFailure),
      });
    },
  });

  const handleSubmit = ({ title }: { title: string }) => {
    updateTitleMutation.mutate({
      experienceId: experienceIdNum,
      body: { title },
    });
  };

  const initialValues = experience
    ? { title: experience?.title || '' }
    : undefined;

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <IconButton
              onClick={gotoExperienceDetails}
              color="inherit"
              size="large"
              aria-label={intl.formatMessage(messages.back)}
            >
              <CloseIcon />
            </IconButton>
            <Typography
              variant="h6"
              style={{ marginLeft: theme.spacing(2) }}
              sx={{
                color: 'inherit',
              }}
            >
              <FormattedMessage {...messages.editExperienceTitleHeader} />
            </Typography>
          </Toolbar>
        </AppBar>
      }
      footer={
        <Toolbar>
          <Button
            disabled={updateTitleMutation.isPending}
            color="primary"
            type="submit"
            form="edit-title-form"
          >
            {updateTitleMutation.isPending ? (
              <FormattedMessage {...generalMessages.loading} />
            ) : (
              <FormattedMessage {...messages.saveEventButton} />
            )}
          </Button>
        </Toolbar>
      }
      isLoading={isLoading}
    >
      {initialValues && (
        <EditTitleForm onSubmit={handleSubmit} initialValues={initialValues} />
      )}
    </ContentView>
  );
};

export default EditTitle;
